import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { MasterSelect } from "@/components/MasterSelect";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { CheckSquare, Download, FileSpreadsheet, MessageCircle, Pencil, Trash2 } from "lucide-react";
import { WhatsAppSendDialog } from "@/components/WhatsAppSendDialog";
import { toDMY } from "@/lib/dates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const MODES = [
  {
    key: "after_discount",
    label: "Discount After Discount",
    hint: "Total discount you want — the bill's own discount is knocked off, the rest goes on the taxable amount",
  },
  {
    key: "direct",
    label: "Direct Discount",
    hint: "Total discount on gross, minus the discount already given in the bill",
  },
];

const SCOPES = [
  { key: "bill", label: "Whole bill", hint: "One rate for everything" },
  { key: "item", label: "Item wise", hint: "A different rate for each item" },
  { key: "company", label: "Company wise", hint: "A different rate for each company" },
];

const rs = (v) => `₹${Number(v || 0).toFixed(2)}`;
const modeLabel = (m) => MODES.find((x) => x.key === m)?.label || m;

export default function Hisab() {
  const [profile, setProfile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [masterCustomers, setMasterCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [bills, setBills] = useState([]);
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState("after_discount");
  const [scope, setScope] = useState("bill");
  const [pct, setPct] = useState("");
  const [amount, setAmount] = useState("");
  const [rates, setRates] = useState({});
  const [notes, setNotes] = useState("");
  const [roundTo, setRoundTo] = useState(0);
  const [preview, setPreview] = useState(null);
  const [waRow, setWaRow] = useState(null);
  const [waFile, setWaFile] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [savedRates, setSavedRates] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [hisabDate, setHisabDate] = useState(new Date().toISOString().slice(0, 10));
  const skipAutoRates = useRef(false);

  const loadSummary = () => api.get("/hisab/customers").then(({ data }) => setCustomers(data));
  const loadHistory = () => api.get("/hisab").then(({ data }) => setHistory(data));

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/masters/customer").then(({ data }) => setMasterCustomers(data.map((m) => m.name))).catch(() => {});
    loadSummary();
    loadHistory();
  }, []);

  useEffect(() => {
    setPicked([]);
    setPreview(null);
    if (!customer) return setBills([]);
    if (!skipAutoRates.current) setRates({});
    api
      .get("/hisab/bills", { params: { customer, hisab_id: editingId || "" } })
      .then(({ data }) => setBills(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    if (skipAutoRates.current) {
      skipAutoRates.current = false;
      return;
    }
    api
      .get(`/customers/${encodeURIComponent(customer)}/discounts`)
      .then(({ data }) => {
        setSavedRates(data);
        if (data.rates?.length) {
          setMode(data.mode || "after_discount");
          setScope("company");
          setRates(Object.fromEntries(data.rates.map((r) => [r.company.toUpperCase(), { pct: String(r.pct), amount: "" }])));
          toast.success("Saved company wise rates applied");
        }
      })
      .catch(() => setSavedRates(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const resetForm = () => {
    setEditingId(null);
    setPct("");
    setAmount("");
    setRates({});
    setNotes("");
    setRoundTo(0);
    setPreview(null);
    setPicked([]);
  };

  const startEdit = (h) => {
    skipAutoRates.current = true;
    setEditingId(h.id);
    setMode(h.mode || "after_discount");
    setScope(h.scope || "bill");
    setPct(h.extra_pct ? String(h.extra_pct) : "");
    setAmount(h.extra_amount ? String(h.extra_amount) : "");
    setNotes(h.notes || "");
    setRoundTo(h.round_to || 0);
    setHisabDate(h.date || new Date().toISOString().slice(0, 10));
    setRates(
      Object.fromEntries(
        (h.rates || []).map((r) => [r.key, { pct: r.pct ? String(r.pct) : "", amount: r.amount ? String(r.amount) : "" }])
      )
    );
    setCustomer(h.customer);
    api
      .get("/hisab/bills", { params: { customer: h.customer, hisab_id: h.id } })
      .then(({ data }) => {
        setBills(data);
        setPicked((h.lines || []).map((l) => l.txn_id));
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Editing this hisab — change the rates and save");
  };

  const scopeBills = useMemo(
    () => (picked.length ? bills.filter((b) => picked.includes(b.id)) : bills),
    [bills, picked]
  );

  // rows the user can rate individually - items or companies present in the chosen bills
  const rateRows = useMemo(() => {
    if (scope === "bill") return [];
    const map = new Map();
    scopeBills.forEach((b) =>
      (b.items || []).forEach((it) => {
        const key = scope === "item" ? it.fabric_id : (it.company_name || "").trim().toUpperCase();
        if (!key) return;
        const label =
          scope === "item" ? `${it.quality_name} · shade ${it.shade_number}` : it.company_name || "-";
        const prev = map.get(key);
        map.set(key, {
          key,
          label,
          taxable: (prev?.taxable || 0) + Number(it.taxable ?? it.amount ?? 0),
          gross: (prev?.gross || 0) + Number(it.gross ?? it.amount ?? 0),
        });
      })
    );
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [scopeBills, scope]);

  const ratePayload = () =>
    Object.entries(rates)
      .map(([key, v]) => ({ key, pct: parseFloat(v?.pct) || 0, amount: parseFloat(v?.amount) || 0 }))
      .filter((r) => r.pct || r.amount);

  const setRate = (key, field, value) =>
    setRates((r) => ({
      ...r,
      [key]: field === "pct" ? { pct: value, amount: "" } : { pct: "", amount: value },
    }));

  const body = () => ({
    customer,
    mode,
    scope,
    notes,
    date: hisabDate,
    extra_pct: scope === "bill" ? parseFloat(pct) || 0 : 0,
    extra_amount: scope === "bill" ? parseFloat(amount) || 0 : 0,
    rates: scope === "bill" ? [] : ratePayload(),
    round_to: roundTo,
    txn_ids: picked,
  });

  const runPreview = async () => {
    if (!customer) return toast.error("Pick a customer");
    if (scope === "bill" && !pct && !amount) return toast.error("Enter the extra discount in % or ₹");
    if (scope !== "bill" && ratePayload().length === 0)
      return toast.error(`Enter a discount for at least one ${scope === "item" ? "item" : "company"}`);
    try {
      const { data } = await api.post("/hisab/preview", body());
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await api.put(`/hisab/${editingId}`, body());
      else await api.post("/hisab", body());
      toast.success(editingId ? "Hisab updated" : "Hisab saved");
      const cust = customer;
      resetForm();
      api.get("/hisab/bills", { params: { customer: cust } }).then(({ data }) => setBills(data));
      loadSummary();
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const download = async (id, format) => {
    try {
      const res = await api.get(`/hisab/${id}/export`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hisab-statement.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const sendOnWhatsApp = async (h) => {
    try {
      const res = await api.get(`/hisab/${h.id}/export`, { params: { format: "pdf" }, responseType: "blob" });
      setWaFile(new File([res.data], `hisab-${h.customer}.pdf`, { type: "application/pdf" }));
      setWaRow(h);
    } catch {
      toast.error("Could not prepare the hisab statement");
    }
  };

  const remove = async () => {    try {
      await api.delete(`/hisab/${deleteRow.id}`);
      toast.success("Hisab removed, bills are open again");
      setDeleteRow(null);
      loadSummary();
      loadHistory();
      if (customer) api.get("/hisab/bills", { params: { customer } }).then(({ data }) => setBills(data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Hisab</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Customer Hisab</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-500">
            Settle a customer's sale bills and give extra discount on top of whatever discount the bill already had —
            one rate for the whole bill, or a different rate for each item or company.
          </p>
        </div>

        {customers.length > 0 && (
          <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <div className="mt-6 overflow-x-auto border border-zinc-200 bg-white">
            <table className="w-full min-w-[600px] text-left text-sm" data-testid="hisab-customer-summary">
              <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                <tr>
                  {["Customer", "Bills", "Billed", "Pending bills", "Pending amount", ""].map((h) => (
                    <th key={h} className="px-3 py-2 font-bold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {customers.map((c) => (
                  <tr key={c.customer} data-testid={`hisab-summary-${c.customer}`}>
                    <td className="px-3 py-2 font-semibold uppercase">{c.customer}</td>
                    <td className="px-3 py-2 font-mono-num">{c.bills}</td>
                    <td className="px-3 py-2 font-mono-num">{rs(c.total)}</td>
                    <td className="px-3 py-2 font-mono-num">{c.pending_bills}</td>
                    <td className="px-3 py-2 font-mono-num font-semibold">{rs(c.pending)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        data-testid={`hisab-pick-${c.customer}`}
                        size="sm"
                        variant="outline"
                        className="rounded-sm"
                        onClick={() => setCustomer(c.customer)}
                      >
                        Do hisab
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
          <div className="border border-zinc-200 bg-white p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <L>Hisab date</L>
                <DateInput data-testid="hisab-date-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={hisabDate} onChange={(v) => setHisabDate(v)} />
              </div>
              <div>
                <L>Customer</L>
                <MasterSelect
                  testId="hisab-customer-select"
                  options={masterCustomers}
                  value={customer}
                  onChange={setCustomer}
                  placeholder="Select customer"
                  emptyHint="Add customers in Admin → Customer Master"
                />
              </div>
              <div>
                <L>Note</L>
                <Input
                  data-testid="hisab-notes-input"
                  className="mt-2 rounded-sm"
                  placeholder="Settlement note"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between">
                <L>Bills ({bills.length} open)</L>
                {bills.length > 0 && (
                  <Button
                    data-testid="hisab-select-all-button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setPicked(picked.length === bills.length ? [] : bills.map((b) => b.id))}
                  >
                    <CheckSquare size={14} className="mr-1.5" />
                    {picked.length === bills.length && bills.length ? "Clear" : "Select all"}
                  </Button>
                )}
              </div>
              {!customer ? (
                <p className="mt-3 text-sm text-zinc-500">Pick a customer to see their open sale bills.</p>
              ) : bills.length === 0 ? (
                <p data-testid="hisab-no-bills" className="mt-3 text-sm text-zinc-500">
                  No open bills for this customer — everything is already in a hisab.
                </p>
              ) : (
                <div className="mt-3 divide-y divide-zinc-200 border border-zinc-200">
                  {bills.map((b) => (
                    <label
                      key={b.id}
                      data-testid={`hisab-bill-${b.ref_no}`}
                      className="flex cursor-pointer items-center gap-3 p-3 hover:bg-zinc-50"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-zinc-950"
                        checked={picked.includes(b.id)}
                        onChange={(e) =>
                          setPicked((p) => (e.target.checked ? [...p, b.id] : p.filter((x) => x !== b.id)))
                        }
                      />
                      <span className="flex-1 text-sm">
                        <span className="font-semibold">{b.ref_no}</span>
                        <span className="text-zinc-500"> · {toDMY(b.date)}</span>
                      </span>
                      <span className="font-mono-num text-xs text-zinc-600">
                        gross {rs(b.gross_amount)} · disc {rs(b.total_discount)} · taxable{" "}
                        {rs(b.taxable_amount)} · net <span className="font-semibold text-zinc-950">{rs(b.grand_total)}</span>
                      </span>
                    </label>
                  ))}
                  <p className="p-3 text-xs text-zinc-500">
                    {picked.length ? `${picked.length} bill(s) selected` : "Nothing ticked — all open bills will be settled"}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6">
              <L>Formula</L>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    data-testid={`hisab-mode-${m.key}`}
                    onClick={() => setMode(m.key)}
                    className={`border p-3 text-left transition-colors duration-200 ${
                      mode === m.key ? "border-zinc-950 bg-[var(--mod-primary)] text-white" : "border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    <p className="text-sm font-bold uppercase tracking-tight">{m.label}</p>
                    <p className={`mt-1 text-xs ${mode === m.key ? "text-zinc-300" : "text-zinc-500"}`}>{m.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <L>Apply rate</L>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {SCOPES.map((sc) => (
                  <button
                    key={sc.key}
                    type="button"
                    data-testid={`hisab-scope-${sc.key}`}
                    onClick={() => {
                      setScope(sc.key);
                      setPreview(null);
                    }}
                    className={`border p-3 text-left transition-colors duration-200 ${
                      scope === sc.key ? "border-zinc-950 bg-[var(--mod-primary)] text-white" : "border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    <p className="text-sm font-bold uppercase tracking-tight">{sc.label}</p>
                    <p className={`mt-1 text-xs ${scope === sc.key ? "text-zinc-300" : "text-zinc-500"}`}>{sc.hint}</p>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-zinc-500">
                Now doing: <span className="font-semibold text-zinc-950">{modeLabel(mode)}</span>
                {scope !== "bill" ? ` — ${scope === "item" ? "item wise" : "company wise"}` : ""}
              </p>
            </div>

            {scope === "bill" ? (
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <L>Extra discount %</L>
                  <Input
                    data-testid="hisab-pct-input"
                    type="number"
                    step="any"
                    className="mt-2 rounded-sm"
                    placeholder="0"
                    value={pct}
                    onChange={(e) => {
                      setPct(e.target.value);
                      setAmount("");
                    }}
                  />
                </div>
                <div>
                  <L>Or extra discount ₹</L>
                  <Input
                    data-testid="hisab-amount-input"
                    type="number"
                    step="any"
                    className="mt-2 rounded-sm"
                    placeholder="0"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      setPct("");
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="mt-5" data-testid={`hisab-rate-table-${scope}`}>
                <L>{scope === "item" ? "Rate for each item" : "Rate for each company"}</L>
                {rateRows.length === 0 ? (
                  <p className="mt-2 text-sm text-zinc-500">
                    Pick a customer with open bills to list their {scope === "item" ? "items" : "companies"}.
                  </p>
                ) : (
                  <div className="mt-2 overflow-x-auto border border-zinc-200">
                    <table className="w-full min-w-[560px] text-left text-sm">
                      <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                        <tr>
                          <th className="px-3 py-2 font-bold">{scope === "item" ? "Item" : "Company"}</th>
                          <th className="px-3 py-2 font-bold">Taxable</th>
                          <th className="px-3 py-2 font-bold">Discount %</th>
                          <th className="px-3 py-2 font-bold">Or ₹</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-200">
                        {rateRows.map((r) => (
                          <tr key={r.key} data-testid={`hisab-rate-row-${r.key}`}>
                            <td className="px-3 py-2 font-semibold uppercase">{r.label}</td>
                            <td className="px-3 py-2 font-mono-num text-zinc-600">{rs(r.taxable)}</td>
                            <td className="px-3 py-2">
                              <Input
                                data-testid={`hisab-rate-pct-${r.key}`}
                                type="number"
                                step="any"
                                className="h-9 w-24 rounded-sm"
                                placeholder="0"
                                value={rates[r.key]?.pct ?? ""}
                                onChange={(e) => setRate(r.key, "pct", e.target.value)}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                data-testid={`hisab-rate-amount-${r.key}`}
                                type="number"
                                step="any"
                                className="h-9 w-24 rounded-sm"
                                placeholder="0"
                                value={rates[r.key]?.amount ?? ""}
                                onChange={(e) => setRate(r.key, "amount", e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="mt-2 text-xs text-zinc-500">
                  Leave a row blank to give it no extra discount.
                </p>
              </div>
            )}
            <div className="mt-5">
              <L>Round off payable</L>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="hisab-round-options">
                {[
                  [0, "No round off"],
                  [1, "₹1"],
                  [5, "₹5"],
                  [10, "₹10"],
                  [50, "₹50"],
                  [100, "₹100"],
                ].map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`hisab-round-${v}`}
                    onClick={() => setRoundTo(v)}
                    className={`border px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${
                      roundTo === v
                        ? "border-zinc-950 bg-[var(--mod-primary)] text-white"
                        : "border-zinc-300 bg-white text-zinc-600 hover:border-zinc-950"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-zinc-500">
                Rounds the final payable to the nearest amount — the difference shows as round off.
              </p>
            </div>


            <div className="mt-5 flex flex-wrap gap-2">
              {savedRates?.rates?.length > 0 && (
                <Button
                  data-testid="hisab-apply-saved-button"
                  variant="outline"
                  className="rounded-sm border-zinc-950"
                  onClick={() => {
                    setMode(savedRates.mode || "after_discount");
                    setScope("company");
                    setRates(
                      Object.fromEntries(
                        savedRates.rates.map((r) => [r.company.toUpperCase(), { pct: String(r.pct), amount: "" }])
                      )
                    );
                    toast.success("Saved rates applied");
                  }}
                >
                  Apply saved rates
                </Button>
              )}
              <Button data-testid="hisab-calculate-button" variant="outline" className="rounded-sm" onClick={runPreview}>
                Calculate
              </Button>
              <Button
                data-testid="hisab-save-button"
                data-f2-save
                className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
                disabled={saving || !preview}
                onClick={save}
              >
                {saving ? "Saving…" : editingId ? "Update hisab" : "Save hisab"}
              </Button>
              {editingId && (
                <Button
                  data-testid="hisab-cancel-edit-button"
                  variant="outline"
                  className="rounded-sm"
                  onClick={resetForm}
                >
                  Cancel edit
                </Button>
              )}
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-5" data-testid="hisab-preview-panel">
            <L>Working</L>
            {!preview ? (
              <p className="mt-3 text-sm text-zinc-500">
                Choose the formula, set the rates and press Calculate.
              </p>
            ) : (
              <>
                <div className="font-mono-num mt-3 space-y-1 text-sm">
                  {[
                    ["Bills", preview.totals.bills],
                    ["Gross", rs(preview.totals.gross)],
                    ["Bill discount", `- ${rs(preview.totals.bill_discount)}`],
                    ["Taxable", rs(preview.totals.taxable)],
                    ["Discount applied on", rs(preview.totals.discount_base)],
                    ["Bill net (with GST)", rs(preview.totals.bill_net)],
                    ["Extra discount", `- ${rs(preview.totals.extra_discount)}`],
                    ...(preview.totals.round_off
                      ? [["Round off", `${preview.totals.round_off > 0 ? "+ " : "- "}${rs(Math.abs(preview.totals.round_off))}`]]
                      : []),
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-4">
                      <span className="text-zinc-500">{label}</span>
                      <span>{value}</span>
                    </div>
                  ))}
                  <div className="flex justify-between gap-4 border-t border-zinc-200 pt-1 text-base font-semibold">
                    <span>Payable</span>
                    <span data-testid="hisab-payable">{rs(preview.totals.payable)}</span>
                  </div>
                </div>

                {preview.breakdown?.length > 0 && (
                  <div className="mt-4 space-y-1 border-t border-dashed border-zinc-200 pt-3" data-testid="hisab-breakdown">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Rate applied</p>
                    {preview.breakdown.map((r) => (
                      <p key={r.key} className="font-mono-num text-xs text-zinc-600">
                        {r.label} @ {r.pct ? `${r.pct}%` : rs(r.amount)} on {rs(r.taxable)} ={" "}
                        <span className="font-semibold text-zinc-950">- {rs(r.extra_discount)}</span>
                      </p>
                    ))}
                  </div>
                )}

                <div className="mt-4 space-y-2 border-t border-dashed border-zinc-200 pt-3">
                  {preview.lines.map((l) => (
                    <p key={l.txn_id} className="font-mono-num text-xs text-zinc-600">
                      {l.ref_no}: net {rs(l.bill_net)} − extra {rs(l.extra_discount)} ={" "}
                      <span className="font-semibold text-zinc-950">{rs(l.payable)}</span>
                    </p>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="font-head text-lg font-bold uppercase tracking-tight">Saved hisab</h2>
          {history.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-500">Nothing settled yet.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {history.map((h) => (
                <div
                  key={h.id}
                  data-testid={`hisab-history-${h.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 border border-zinc-200 bg-white p-4"
                >
                  <div>
                    <p className="font-head text-sm font-bold uppercase tracking-tight">{h.customer}</p>
                    <p className="mt-0.5 text-xs text-zinc-500">
                      {toDMY(h.date)} · {modeLabel(h.mode)}
                      {h.scope && h.scope !== "bill" ? ` (${h.scope} wise)` : ""} ·{" "}
                      {h.scope === "bill" ? (h.extra_pct ? `${h.extra_pct}%` : rs(h.extra_amount)) : `${(h.breakdown || []).length} rate(s)`}{" "}
                      · {h.totals.bills} bill(s)
                    </p>
                    <p className="font-mono-num mt-1 text-xs text-zinc-600">
                      net {rs(h.totals.bill_net)} − extra {rs(h.totals.extra_discount)} ={" "}
                      <span className="font-semibold text-zinc-950">{rs(h.totals.payable)}</span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`hisab-pdf-${h.id}`}
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => download(h.id, "pdf")}
                    >
                      <Download size={14} className="mr-1.5" /> PDF
                    </Button>
                    <Button
                      data-testid={`hisab-excel-${h.id}`}
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => download(h.id, "excel")}
                    >
                      <FileSpreadsheet size={14} className="mr-1.5" /> Excel
                    </Button>
                    <Button
                      data-testid={`hisab-whatsapp-${h.id}`}
                      size="sm"
                      variant="outline"
                      className="rounded-sm border-[#128C7E] text-[#0f7568]"
                      onClick={() => sendOnWhatsApp(h)}
                    >
                      <MessageCircle size={14} className="mr-1.5" /> WhatsApp
                    </Button>
                    <Button
                      data-testid={`hisab-edit-${h.id}`}
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => startEdit(h)}
                    >
                      <Pencil size={14} className="mr-1.5" /> Edit
                    </Button>
                    <Button
                      data-testid={`hisab-delete-${h.id}`}
                      size="sm"
                      variant="outline"
                      className="rounded-sm text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteRow(h)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <WhatsAppSendDialog
        open={!!waRow}
        onOpenChange={(o) => {
          if (!o) {
            setWaRow(null);
            setWaFile(null);
          }
        }}
        title="Send hisab on WhatsApp"
        defaultCustomer={waRow?.customer || ""}
        file={waFile}
        fileLabel="hisab statement"
        message={
          waRow
            ? `Namaste ${waRow.customer}, sharing your hisab statement — ${waRow.totals.bills} bill(s), net ${rs(
                waRow.totals.bill_net
              )}, extra discount ${rs(waRow.totals.extra_discount)}, payable ${rs(waRow.totals.payable)}. Kindly confirm.`
            : ""
        }
      />

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this hisab?</AlertDialogTitle>            <AlertDialogDescription>
              The bills in it become open again so you can redo the hisab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hisab-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="hisab-delete-confirm" onClick={remove} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
